import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, PowerOff, Power } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import PageWrapper from '@/components/layout/PageWrapper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { pmTypesApi, pmDataSourcesApi, pmAssignmentsApi } from '@/api';
import { toast } from '@/hooks/use-toast';
import type { PmType, PmDataSource, PmAssignment } from '@/types';

// ── PM Type form schema ────────────────────────────────────────────────────────

const pmTypeSchema = z.object({
  name: z.string().min(1, 'Name is required').max(128),
  description: z.string().optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Must be a hex color').default('#6366f1')
    .refine(
      (v) => !['#3b82f6', '#22c55e', '#ef4444'].includes(v.toLowerCase()),
      'This color is reserved for calendar system events (repairs, completed, overdue)',
    ),
  triggerType: z.enum(['time', 'uom']).default('time'),
  scheduleMode: z.enum(['interval', 'day_of_month', 'day_of_week_of_month']).optional(),
  intervalDays: z.coerce.number().int().positive().optional(),
  anchoredToStart: z.boolean().default(true),
  weekOfMonth: z.coerce.number().int().min(1).max(5).optional(),
  dayOfWeek: z.coerce.number().int().min(0).max(6).optional(),
  monthFrequency: z.coerce.number().int().positive().default(1),
  alertDaysBefore: z.coerce.number().int().min(0).default(7),
  uomLabel: z.string().max(64).optional(),
  intervalUom: z.coerce.number().positive().optional(),
});

type PmTypeFormData = z.infer<typeof pmTypeSchema>;

const DOW_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WOM_LABELS = ['1st', '2nd', '3rd', '4th', 'Last'];

// ── PM Type dialog ─────────────────────────────────────────────────────────────

function PmTypeDialog({
  open,
  onOpenChange,
  existing,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  existing?: PmType | null;
}) {
  const queryClient = useQueryClient();

  const form = useForm<PmTypeFormData>({
    resolver: zodResolver(pmTypeSchema),
    defaultValues: existing
      ? {
          name: existing.name,
          description: existing.description ?? '',
          color: existing.color,
          triggerType: existing.triggerType,
          scheduleMode: existing.scheduleMode ?? undefined,
          intervalDays: existing.intervalDays ?? undefined,
          anchoredToStart: existing.anchoredToStart,
          weekOfMonth: existing.weekOfMonth ?? undefined,
          dayOfWeek: existing.dayOfWeek ?? undefined,
          monthFrequency: existing.monthFrequency,
          alertDaysBefore: existing.alertDaysBefore,
          uomLabel: existing.uomLabel ?? '',
          intervalUom: existing.intervalUom ? Number(existing.intervalUom) : undefined,
        }
      : {
          name: '',
          description: '',
          color: '#6366f1',
          triggerType: 'time',
          scheduleMode: 'interval',
          intervalDays: 90,
          anchoredToStart: true,
          monthFrequency: 1,
          alertDaysBefore: 7,
          uomLabel: '',
        },
  });

  // Reset form whenever the dialog opens with a different PM type
  useEffect(() => {
    if (!open) return;
    if (existing) {
      form.reset({
        name: existing.name,
        description: existing.description ?? '',
        color: existing.color,
        triggerType: existing.triggerType,
        scheduleMode: existing.scheduleMode ?? undefined,
        intervalDays: existing.intervalDays ?? undefined,
        anchoredToStart: existing.anchoredToStart,
        weekOfMonth: existing.weekOfMonth ?? undefined,
        dayOfWeek: existing.dayOfWeek ?? undefined,
        monthFrequency: existing.monthFrequency,
        alertDaysBefore: existing.alertDaysBefore,
        uomLabel: existing.uomLabel ?? '',
        intervalUom: existing.intervalUom ? Number(existing.intervalUom) : undefined,
      });
    } else {
      form.reset({
        name: '',
        description: '',
        color: '#6366f1',
        triggerType: 'time',
        scheduleMode: 'interval',
        intervalDays: 90,
        anchoredToStart: true,
        monthFrequency: 1,
        alertDaysBefore: 7,
        uomLabel: '',
      });
    }
  }, [open, existing]);

  const triggerType = form.watch('triggerType');
  const scheduleMode = form.watch('scheduleMode');

  const saveMutation = useMutation({
    mutationFn: (data: PmTypeFormData) => {
      const payload = { ...data } as Record<string, unknown>;
      return existing
        ? pmTypesApi.update(existing.id, payload)
        : pmTypesApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pmTypes'] });
      toast({ title: existing ? 'PM type updated' : 'PM type created' });
      onOpenChange(false);
    },
    onError: () => toast({ title: 'Save failed', variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit PM Type' : 'New PM Type'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit((d) => saveMutation.mutate(d))} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl><Input placeholder="e.g. A Service" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <FormField control={form.control} name="color" render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="h-9 w-12 cursor-pointer rounded border border-input p-0.5"
                      />
                      <Input {...field} className="font-mono text-xs" />
                    </div>
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    Reserved (in use by calendar):
                    {[
                      { hex: '#3b82f6', label: 'Repairs' },
                      { hex: '#22c55e', label: 'Completed' },
                      { hex: '#ef4444', label: 'Overdue' },
                    ].map(({ hex, label }) => (
                      <span key={hex} className="ml-2 inline-flex items-center gap-1">
                        <span className="inline-block h-3 w-3 rounded-full border" style={{ backgroundColor: hex }} />
                        {label}
                      </span>
                    ))}
                  </p>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel>Description (optional)</FormLabel>
                <FormControl><Textarea rows={2} {...field} /></FormControl>
              </FormItem>
            )} />

            <FormField control={form.control} name="triggerType" render={({ field }) => (
              <FormItem>
                <FormLabel>Trigger Type</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="time">Time-based</SelectItem>
                    <SelectItem value="uom">UOM-based (unit of measure)</SelectItem>
                  </SelectContent>
                </Select>
              </FormItem>
            )} />

            {triggerType === 'time' && (
              <>
                <FormField control={form.control} name="scheduleMode" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Schedule Mode</FormLabel>
                    <Select value={field.value ?? ''} onValueChange={field.onChange}>
                      <FormControl><SelectTrigger><SelectValue placeholder="Select mode…" /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="interval">Every N days</SelectItem>
                        <SelectItem value="day_of_month">Day of month</SelectItem>
                        <SelectItem value="day_of_week_of_month">Day of week of month</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />

                {scheduleMode === 'interval' && (
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="intervalDays" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Interval (days)</FormLabel>
                        <FormControl><Input type="number" min={1} {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="anchoredToStart" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Anchoring</FormLabel>
                        <Select value={field.value ? 'true' : 'false'} onValueChange={(v) => field.onChange(v === 'true')}>
                          <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                          <SelectContent>
                            <SelectItem value="true">From start date</SelectItem>
                            <SelectItem value="false">From last completion</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )} />
                  </div>
                )}

                {scheduleMode === 'day_of_month' && (
                  <div className="rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">Day of month</span> — the specific day (1–31) is set per equipment when assigning this PM type.
                    <div className="mt-2">
                      <FormField control={form.control} name="monthFrequency" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Every N months</FormLabel>
                          <FormControl><Input type="number" min={1} className="w-28" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>
                  </div>
                )}

                {scheduleMode === 'day_of_week_of_month' && (
                  <div className="grid grid-cols-3 gap-3">
                    <FormField control={form.control} name="weekOfMonth" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Week</FormLabel>
                        <Select value={String(field.value ?? '')} onValueChange={(v) => field.onChange(Number(v))}>
                          <FormControl><SelectTrigger><SelectValue placeholder="Week…" /></SelectTrigger></FormControl>
                          <SelectContent>
                            {WOM_LABELS.map((label, i) => (
                              <SelectItem key={i + 1} value={String(i + 1)}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="dayOfWeek" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Day</FormLabel>
                        <Select value={String(field.value ?? '')} onValueChange={(v) => field.onChange(Number(v))}>
                          <FormControl><SelectTrigger><SelectValue placeholder="Day…" /></SelectTrigger></FormControl>
                          <SelectContent>
                            {DOW_LABELS.map((label, i) => (
                              <SelectItem key={i} value={String(i)}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="monthFrequency" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Every N months</FormLabel>
                        <FormControl><Input type="number" min={1} {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                )}

                <FormField control={form.control} name="alertDaysBefore" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Days Before to Create Repair Order</FormLabel>
                    <FormControl><Input type="number" min={0} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </>
            )}

            {triggerType === 'uom' && (
              <div className="grid grid-cols-2 gap-3">
                <FormField control={form.control} name="uomLabel" render={({ field }) => (
                  <FormItem>
                    <FormLabel>UOM Label</FormLabel>
                    <FormControl><Input placeholder="e.g. miles, hours" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="intervalUom" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Interval</FormLabel>
                    <FormControl><Input type="number" min={0} step="any" placeholder="e.g. 10000" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving…' : existing ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ── PM Types tab ───────────────────────────────────────────────────────────────

function PmTypesTab() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PmType | null>(null);
  const [filterActive, setFilterActive] = useState<'active' | 'inactive' | 'all'>('active');
  const [filterTrigger, setFilterTrigger] = useState<string>('all');
  const [filterSchedule, setFilterSchedule] = useState<string>('all');
  const [confirmDisable, setConfirmDisable] = useState<{ pt: PmType; assignments: PmAssignment[] } | null>(null);
  const [loadingDisableId, setLoadingDisableId] = useState<number | null>(null);

  const { data: pmTypes = [], isLoading } = useQuery({
    queryKey: ['pmTypes'],
    queryFn: pmTypesApi.list,
  });

  const toggleMutation = useMutation({
    mutationFn: (pt: PmType) => pmTypesApi.update(pt.id, { isActive: !pt.isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pmTypes'] });
      queryClient.invalidateQueries({ queryKey: ['calendar'] });
      setConfirmDisable(null);
    },
    onError: () => toast({ title: 'Failed to update status', variant: 'destructive' }),
  });

  async function handleToggleClick(pt: PmType) {
    // Enabling is always safe — no confirmation needed
    if (!pt.isActive) {
      toggleMutation.mutate(pt);
      return;
    }
    // Disabling: fetch active assignments for this PM type first
    setLoadingDisableId(pt.id);
    try {
      const all = await pmAssignmentsApi.list({ active: true });
      const affected = all.filter((a) => a.pmTypeId === pt.id);
      setConfirmDisable({ pt, assignments: affected });
    } catch {
      toast({ title: 'Failed to load assignment data', variant: 'destructive' });
    } finally {
      setLoadingDisableId(null);
    }
  }

  function scheduleSummary(pt: PmType): string {
    if (pt.triggerType === 'uom') {
      return `Every ${pt.intervalUom ?? '?'} ${pt.uomLabel ?? 'units'}`;
    }
    if (pt.scheduleMode === 'interval') {
      return `Every ${pt.intervalDays ?? '?'} days`;
    }
    if (pt.scheduleMode === 'day_of_month') {
      return `Day ${pt.dayOfMonth} every ${pt.monthFrequency} mo`;
    }
    if (pt.scheduleMode === 'day_of_week_of_month') {
      const week = pt.weekOfMonth === 5 ? 'Last' : WOM_LABELS[(pt.weekOfMonth ?? 1) - 1];
      const day = DOW_LABELS[pt.dayOfWeek ?? 0];
      return `${week} ${day} every ${pt.monthFrequency} mo`;
    }
    return '—';
  }

  const filtered = useMemo(() => {
    return pmTypes.filter((pt) => {
      if (filterActive === 'active' && !pt.isActive) return false;
      if (filterActive === 'inactive' && pt.isActive) return false;
      if (filterTrigger !== 'all' && pt.triggerType !== filterTrigger) return false;
      if (filterSchedule !== 'all' && (pt.scheduleMode ?? 'none') !== filterSchedule) return false;
      return true;
    });
  }, [pmTypes, filterActive, filterTrigger, filterSchedule]);

  const hasFilters = filterActive !== 'active' || filterTrigger !== 'all' || filterSchedule !== 'all';

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-3">
          <Select value={filterActive} onValueChange={(v) => setFilterActive(v as 'active' | 'inactive' | 'all')}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterTrigger} onValueChange={setFilterTrigger}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Trigger" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Triggers</SelectItem>
              <SelectItem value="time">Time-based</SelectItem>
              <SelectItem value="uom">UOM-based</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterSchedule} onValueChange={setFilterSchedule}>
            <SelectTrigger className="w-52">
              <SelectValue placeholder="Schedule" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Schedules</SelectItem>
              <SelectItem value="interval">Every N days</SelectItem>
              <SelectItem value="day_of_month">Day of month</SelectItem>
              <SelectItem value="day_of_week_of_month">Day of week of month</SelectItem>
            </SelectContent>
          </Select>

          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setFilterActive('active'); setFilterTrigger('all'); setFilterSchedule('all'); }}
            >
              Clear filters
            </Button>
          )}
        </div>

        <Button onClick={() => { setEditing(null); setDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />New PM Type
        </Button>
      </div>

      <div className="rounded-md border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Trigger</TableHead>
              <TableHead>Schedule</TableHead>
              <TableHead>Alert Before</TableHead>
              <TableHead>Color</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <TableCell key={j}><div className="h-4 animate-pulse rounded bg-muted" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                  {pmTypes.length === 0 ? 'No PM types defined yet.' : 'No PM types match the current filters.'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((pt) => (
                <TableRow key={pt.id} className={!pt.isActive ? 'opacity-50' : undefined}>
                  <TableCell className="font-medium">{pt.name}</TableCell>
                  <TableCell className="text-muted-foreground capitalize">{pt.triggerType}</TableCell>
                  <TableCell className="text-muted-foreground">{scheduleSummary(pt)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {pt.triggerType === 'time' ? `${pt.alertDaysBefore}d` : '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block h-4 w-4 rounded-full border"
                        style={{ backgroundColor: pt.color }}
                      />
                      <span className="font-mono text-xs text-muted-foreground">{pt.color}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={pt.isActive ? 'text-green-600 dark:text-green-400 text-sm' : 'text-muted-foreground text-sm'}>
                      {pt.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        title={pt.isActive ? 'Disable' : 'Enable'}
                        disabled={loadingDisableId === pt.id || toggleMutation.isPending}
                        onClick={() => handleToggleClick(pt)}
                      >
                        {pt.isActive
                          ? <PowerOff className="h-3.5 w-3.5 text-muted-foreground" />
                          : <Power className="h-3.5 w-3.5 text-green-600" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setEditing(pt); setDialogOpen(true); }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PmTypeDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}
        existing={editing}
      />

      {/* Disable confirmation dialog */}
      <Dialog open={!!confirmDisable} onOpenChange={(v) => { if (!v) setConfirmDisable(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Disable PM Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1 text-sm">
            <p>
              Are you sure you want to disable <span className="font-semibold">{confirmDisable?.pt.name}</span>?
              Future calendar occurrences will be hidden.
            </p>
            {confirmDisable && confirmDisable.assignments.length > 0 ? (
              <div>
                <p className="text-muted-foreground mb-2">
                  Currently assigned to {confirmDisable.assignments.length} piece{confirmDisable.assignments.length !== 1 ? 's' : ''} of equipment:
                </p>
                <ul className="rounded-md border border-border bg-muted/30 divide-y divide-border max-h-48 overflow-y-auto">
                  {confirmDisable.assignments.map((a) => (
                    <li key={a.id} className="px-3 py-1.5 font-mono text-xs">
                      {a.equipment?.unitNumber ?? `#${a.equipmentId}`}
                      {a.equipment?.equipmentType ? <span className="text-muted-foreground ml-2">{a.equipment.equipmentType}</span> : null}
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground text-xs mt-2">
                  These assignments remain intact — re-enabling the PM type will restore their calendar entries.
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground">This PM type has no active assignments.</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDisable(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={toggleMutation.isPending}
              onClick={() => confirmDisable && toggleMutation.mutate(confirmDisable.pt)}
            >
              {toggleMutation.isPending ? 'Disabling…' : 'Disable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Data Sources tab ───────────────────────────────────────────────────────────

const dataSourceSchema = z.object({
  name: z.string().min(1, 'Name is required').max(128),
  sourceType: z.string().min(1).max(64).default('api'),
  isActive: z.boolean().default(true),
});

type DataSourceFormData = z.infer<typeof dataSourceSchema>;

function DataSourcesTab() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PmDataSource | null>(null);

  const { data: sources = [], isLoading } = useQuery({
    queryKey: ['pmDataSources'],
    queryFn: pmDataSourcesApi.list,
  });

  const form = useForm<DataSourceFormData>({
    resolver: zodResolver(dataSourceSchema),
    defaultValues: { name: '', sourceType: 'api', isActive: true },
  });

  const saveMutation = useMutation({
    mutationFn: (data: DataSourceFormData) =>
      editing
        ? pmDataSourcesApi.update(editing.id, data)
        : pmDataSourcesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pmDataSources'] });
      toast({ title: editing ? 'Data source updated' : 'Data source created' });
      setDialogOpen(false);
      setEditing(null);
    },
    onError: () => toast({ title: 'Save failed', variant: 'destructive' }),
  });

  function openEdit(source: PmDataSource) {
    setEditing(source);
    form.reset({ name: source.name, sourceType: source.sourceType, isActive: source.isActive });
    setDialogOpen(true);
  }

  function openNew() {
    setEditing(null);
    form.reset({ name: '', sourceType: 'api', isActive: true });
    setDialogOpen(true);
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={openNew}>
          <Plus className="mr-2 h-4 w-4" />New Data Source
        </Button>
      </div>

      <div className="rounded-md border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 2 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 4 }).map((_, j) => (
                    <TableCell key={j}><div className="h-4 animate-pulse rounded bg-muted" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : sources.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                  No external data sources configured.
                </TableCell>
              </TableRow>
            ) : (
              sources.map((src) => (
                <TableRow key={src.id}>
                  <TableCell className="font-medium">{src.name}</TableCell>
                  <TableCell className="text-muted-foreground font-mono text-sm">{src.sourceType}</TableCell>
                  <TableCell>
                    <span className={src.isActive ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}>
                      {src.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(src)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Data Source' : 'New Data Source'}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit((d) => saveMutation.mutate(d))} className="space-y-4">
              <FormField control={form.control} name="name" render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl><Input placeholder="e.g. Samsara Fleet API" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="sourceType" render={({ field }) => (
                <FormItem>
                  <FormLabel>Source Type</FormLabel>
                  <FormControl><Input placeholder="api" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              {editing && (
                <FormField control={form.control} name="isActive" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select value={field.value ? 'true' : 'false'} onValueChange={(v) => field.onChange(v === 'true')}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value="true">Active</SelectItem>
                        <SelectItem value="false">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )} />
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving…' : editing ? 'Save' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function PMAdmin() {
  return (
    <PageWrapper
      title="PM Admin"
      subtitle="Define PM types and external data sources"
    >
      <Tabs defaultValue="types">
        <TabsList className="mb-6">
          <TabsTrigger value="types">PM Types</TabsTrigger>
          <TabsTrigger value="sources">Data Sources</TabsTrigger>
        </TabsList>
        <TabsContent value="types">
          <PmTypesTab />
        </TabsContent>
        <TabsContent value="sources">
          <DataSourcesTab />
        </TabsContent>
      </Tabs>
    </PageWrapper>
  );
}
